/** @file Pixel anchor of a slot label: centre of its region quad or rigid mesh, else the slot bone origin. */
import type { AttachmentData } from '@pose2d/core/types';
import { slotWorld } from '@pose2d/geometry/slotWorld';
import { affine } from '@pose2d/math/affine';
import type { OverlayInput } from '../overlayInput';

/** Scratch slot world, filled by geometry's slotWorld (bone or override, times the active mount). */
const world = new Float64Array(6);

/** Centroid of the attachment's local points (region corners, rigid mesh vertices) or [0, 0] otherwise. */
function localCentroid(att: AttachmentData | undefined, out: [number, number]): [number, number] {
  const points = att === undefined ? null : att.type === 'region' ? att.local : att.skin.kind === 'rigid' ? att.skin.vertices : null;
  out[0] = 0;
  out[1] = 0;
  if (points === null || points.length < 2) return out;
  const n = Math.floor(points.length / 2);
  for (let i = 0; i < n; i++) {
    out[0] += (points[2 * i] ?? 0) / n;
    out[1] += (points[2 * i + 1] ?? 0) / n;
  }
  return out;
}

/** Writes the anchor of `slot` in pixels into out (skinned meshes and empty slots use the slot bone origin). */
export function slotAnchor(input: OverlayInput, slot: number, out: [number, number]): [number, number] {
  const att = input.data.attachments[input.pose.slotAttachment[slot] ?? -1];
  localCentroid(att, out);
  slotWorld(input.pose, input.data, slot, world);
  affine.apply(world, out[0], out[1], out);
  return affine.apply(input.camera.worldToPixel, out[0], out[1], out);
}
